<template>
  <a-select
    v-model:value="selectedValue"
    :placeholder="placeholder"
    :loading="loading"
    :disabled="disabled"
    :allow-clear="allowClear"
    :mode="mode"
    :style="style"
    @change="handleChange"
    @focus="handleFocus"
  >
    <a-select-option
      v-for="option in options"
      :key="option.value"
      :value="option.value"
      :disabled="option.disabled"
    >
      {{ option.label }}
    </a-select-option>
  </a-select>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { LanguageAPI, type LanguageOption, LanguageType } from '@/modules/system/api/language'

interface Props {
  value?: string | string[]
  languageType?: LanguageType
  placeholder?: string
  disabled?: boolean
  allowClear?: boolean
  mode?: 'multiple' | 'tags'
  style?: Record<string, any>
  includeInactive?: boolean
}

interface Emits {
  (e: 'update:value', value: string | string[]): void
  (e: 'change', value: string | string[], option: LanguageOption | LanguageOption[]): void
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '请选择语言',
  disabled: false,
  allowClear: true,
  includeInactive: false
})

const emit = defineEmits<Emits>()

// 响应式数据
const loading = ref(false)
const options = ref<(LanguageOption & { disabled?: boolean })[]>([])

// 计算属性
const selectedValue = computed({
  get: () => props.value,
  set: (value) => {
    if (value !== undefined) {
      emit('update:value', value)
    }
  }
})

// 方法
const loadOptions = async () => {
  try {
    loading.value = true
    const response = await LanguageAPI.getLanguageOptions({
      language_type: props.languageType,
      status: !props.includeInactive
    })
    options.value = response
  } catch (error) {
    console.error('加载语言选项失败:', error)
    options.value = []
  } finally {
    loading.value = false
  }
}

const handleChange = (value: string | string[], option: any) => {
  emit('change', value, option)
}

const handleFocus = () => {
  if (options.value.length === 0) {
    loadOptions()
  }
}

// 监听语言类型变化
watch(
  () => props.languageType,
  () => {
    loadOptions()
  }
)

// 生命周期
onMounted(() => {
  loadOptions()
})
</script>